import * as React from "react";
import * as DeployActions from "../actions/DeployActions";
import * as LogLevelActions from "../actions/LogLevelActions";
import * as Actions from "../actions/ProcessListActions";
import * as RunOnceActions from "../actions/RunOnceActions";
import * as CleanOldVersionsActions from "../actions/CleanOldVersionsActions";
import * as AppPath from "../model/AppPath";
import { Link } from "react-router-dom";
import download from "downloadjs";
import { connect } from "react-redux";
import { State } from "../model/State";

import { Permissions } from "../../shared/model/AllPermissions";
import { DispatchType, Utilities } from "../../shared/utilities";
import { Process } from "../model/Process";
import { handleToggleDropdown } from "shared/styles/helpers";
import DropdownButton, { DropdownButtonWithState } from "shared/components/DropdownButton";

export const ActionMenu: React.FC<ActionMenuProps> = (props) => {

    const processRowActions = `process-row-actions-${props.processIds}${props.isBatch ? "-batched" : ""}`;

    // If there are no permissions just dont render the menu
    if (!props.permissions[Permissions.ProcessEdit] &&
        !props.permissions[Permissions.ProcessDeploy] &&
        !props.permissions[Permissions.ProcessStart]) {
        return null;
    }

    const elemKey = props.processIds.length > 1 ? "batch" : props.processIds[0];

    const saveAsCSVhandler = async () => {
        try {
            const processExport = await Utilities.getFetchWithHeaders(`api/processList/download/${props.currentAgentGroupName}`, {
                processGuids: props.processGuids,
                subProcesses: props.isSubprocessList ? "true" : "false"
            });
            const fileName = Utilities.parseFileNameFromContentDisposition(processExport.headers.get("Content-Disposition"));
            download(processExport.content as any, fileName ?? "processDownload.csv", processExport.headers.get("Content-Type") ?? "text/csv")
        } catch (e) {
            Utilities.toastError(e);
            console.error(e);
        }
    }

    const getActions = () => {
        const isDefault = props.currentAgentGroupName === DeployActions.DEFAULT_AGENT_GROUP_NAME;
        const exportLimit = 100;
        const canExportProcesses = props.processIds.length <= exportLimit;
        const isBatch = props.processIds.length > 1;
        const menuItems: JSX.Element[] | null = [];
        if (props.permissions[Permissions.ProcessStart]) {
            menuItems.push(
                <button key="run" className="run-process-selector" onClick={handleRunOnce}>{FRENDS.Localizations.RunOnce}</button>
            );
        }
        if (!props.isSubprocessList) {
            if (props.permissions[Permissions.ProcessEdit]) {
                menuItems.push(
                    <button key="activate" className="activate-process-selector" onClick={handleActivate}>{FRENDS.Localizations.Activate}</button>,
                    <button key="deactivate" className="deactivate-process-selector" onClick={handleDeactivate}>{FRENDS.Localizations.Deactivate}</button>,
                    <button key="logsetting" className="logsetting-process-selector" onClick={handleSetLogLevel}>{FRENDS.Localizations.LogSettings}</button>
                );
            }
        }
        if (!props.instanceList) {

            const divider = getDivider(menuItems);
            if (divider !== undefined) {
                menuItems.push(divider);
            }

            if (props.permissions[Permissions.ProcessDeploy]) {
                menuItems.push(
                    <button key="deploy" className="deploy-process-selector" onClick={handleDeploy}>{FRENDS.Localizations.Process_DeployToEnvironment}</button>
                );
            }

            if (props.permissions[Permissions.ProcessEdit]) {
                menuItems.push(
                    isDefault ? <button key="switchProcess" className="switch-process-selector" onClick={handleSwitchVersion}>{FRENDS.Localizations.SwitchProcess}</button> : null as any,
                    isDefault ? <button key="rebuild" className="rebuild-process-selector" onClick={handleRebuildProcesses}>{FRENDS.Localizations.RebuildProcess}</button> : null as any,
                    getDivider(menuItems) as JSX.Element,
                    isDefault ? <button key="copy" className="copy-process-selector" onClick={handleCopyProcesses}>{FRENDS.Localizations.Copy}</button> : null as any,
                    <button key="delete" className="delete-process-selector" onClick={handleDeleteProcesses}>{FRENDS.Localizations.Delete}</button>,
                    canExportProcesses ? <button key="export" className="export-process-selector" onClick={handleExportProcesses}>{FRENDS.Localizations.Export}</button> :
                        <button key="export" className="export-process-selector" disabled={true}>{FRENDS.Localizations.Export} ({FRENDS.Localizations.TooManyProcessesSelected})</button>,
                    <button key="save-csv" className="save-process-csv-selector" onClick={handleSaveAsCSV}>Save as .csv</button>,
                    <button key="edittags" className="edittags-process-selector" onClick={handleOpenTagModal}>{FRENDS.Localizations.EditTags}</button>,
                    <button key="pin" className="pin-process-selector" onClick={handlePin}>Pin</button>
                );
            }

            if (props.permissions[Permissions.ProcessAdmin] && !isBatch) {
                menuItems.push(
                    isDefault ? <button key="cleanup-old-versions" className="cleanup-old-versions-selector" onClick={handleCleanupOldVersions}>{FRENDS.Localizations.Cleanup} old versions</button> : null as any
                )
            }

            if (!isBatch) {
                if (props.permissions[Permissions.ProcessTemplateEdit] && isDefault) {
                    const templateActionsDivider = getDivider(menuItems);
                    if (templateActionsDivider !== undefined) {
                        menuItems.push(templateActionsDivider);
                    }
                    menuItems.push(
                        <button key="create-template-from-process" className="create-template-from-process-selector" onClick={handleCreateTemplateFromProcess}>Update or Create a Template from Process</button>
                    );
                    if (props.hasTemplate) {
                        menuItems.push(
                            <button key="unlink-from-template" className="unlink-from-template-selector" onClick={handleUnlinkTemplate}>Unlink from Template</button>
                        );
                    }
                }
                const divider = getDivider(menuItems);
                if (divider !== undefined) {
                    menuItems.push(divider);
                }
                menuItems.push(
                    <Link to={AppPath.getInstancePath(props.processGuids)} key={`showinstance-${props.processGuids.join("-")}`} className="showinstance-selector">{FRENDS.Localizations.ShowProcessInstances}</Link>
                );
                menuItems.push(
                    <button key="showchangelog" onClick={handleOpenChangeLog} className="showchangelog-process-selector">{FRENDS.Localizations.ShowChangeLog}</button>
                );
                menuItems.push(
                    <a href={`${props.editLink}/documentation`} key={`showdocumentation`} className="showdocumentation-selector">Show Process documentation</a>
                )
            } else {
                const divider = getDivider(menuItems);
                if (divider !== undefined) {
                    menuItems.push(divider);
                }
                menuItems.push(
                    <Link to={AppPath.getInstancePath(props.processGuids)} key={`showinstances-${props.processGuids.join("-")}`} className="showinstances-selector">{FRENDS.Localizations.ShowProcessInstances}</Link>
                );
            }
        }

        if (props.permissions[Permissions.ProcessEdit]) {
            if (props.editLink || props.showProcessVariableEditItem) {
                menuItems.push(getDivider(menuItems) as JSX.Element);
            }

            if (props.editLink) {
                menuItems.push(<a key="edit" href={props.editLink} className="edit-process-selector">{FRENDS.Localizations.Edit}</a>);
            }

            if (props.showProcessVariableEditItem) {
                menuItems.push(<button key="editProcessVariables" className="edit-process-variables-selector" onClick={handleEditProcessVariables}>Edit Process Variables</button>)
            }
        }
        return menuItems;
    }

    const getDivider = (items: JSX.Element[]) => {
        const lastItemIndex = items.length - 1;
        if (items.length && items[lastItemIndex]) {
            if (!items[lastItemIndex].props.divider) {
                return <hr key={"divider" + items.length} />;
            }
        }
        return undefined;
    }

    const getActionClickHandler = (callback: () => void) => {
        return () => {
            props.managedState?.onHide();
            callback();
        };
    };

    const handleSetLogLevel = getActionClickHandler(() => props.onOpenLogLevelDialog(props.processIds));
    const handleRunOnce = getActionClickHandler(() => props.onRunOnce(props.processIds, props.currentAgentGroupName));
    const handleActivate = getActionClickHandler(() => props.onActivate(props.processIds, props.currentAgentGroupName));
    const handleDeactivate = getActionClickHandler(() => props.onDeactivate(props.processIds, props.currentAgentGroupName));
    const handleDeploy = getActionClickHandler(() => props.onOpenDeployDialog(props.processIds, false));
    const handleSwitchVersion = getActionClickHandler(() => props.onOpenDeployDialog(props.processIds, true));
    const handleExportProcesses = getActionClickHandler(() => props.onExportProcesses(props.processIds, props.currentAgentGroupName));
    const handleOpenChangeLog = getActionClickHandler(() => props.onOpenChangeLog(props.processIds[0]));
    const handleEditProcessVariables = getActionClickHandler(() => props.onOpenProcessVariablesEditorDialog(props.processIds[0]));
    const handleRebuildProcesses = getActionClickHandler(() => props.onRebuildProcesses());
    const handleCopyProcesses = getActionClickHandler(() => props.onCopyProcesses());
    const handleDeleteProcesses = getActionClickHandler(() => props.onDeleteProcesses());
    const handleOpenTagModal = getActionClickHandler(() => props.onOpenTagModal());
    const handleSaveAsCSV = getActionClickHandler(() => saveAsCSVhandler());
    const handlePin = getActionClickHandler(() => props.onPin());
    const handleCleanupOldVersions = getActionClickHandler(() => props.onCleanupOldVersions(props.processGuids[0]));
    const handleCreateTemplateFromProcess = getActionClickHandler(() => props.onCreateTemplateFromProcess(props.processIds[0]));
    const handleUnlinkTemplate = getActionClickHandler(() => {
        if (confirm("Are you sure you want to unlink this Process from its Template?")) {
            props.onUnlinkTemplate(props.processIds[0]);
        }
    });

    const getDropdownElement = () => React.cloneElement(props.labelComponent, {
        onClick: () => {
            if (!props.managedState) {
                handleToggleDropdown(processRowActions);
                return;
            }

            if (props.managedState.open) {
                props.managedState.onHide();
            } else {
                props.managedState.onOpen();
            }
        }
    });

    return (
        <>
            {!props.managedState ? (
                <DropdownButton
                    buttonComponent={getDropdownElement()}
                    id={processRowActions}
                    dropDownButtonClassName={"row-actions-menu-button-selector"}
                    dropDownButtonId={"actions-" + elemKey}
                    dropDownContentPullRight={true}
                >
                    {getActions()}
                </DropdownButton>
            ) : (
                <DropdownButtonWithState
                    open={props.managedState.open}
                    onHide={props.managedState.onHide}
                    buttonComponent={getDropdownElement()}
                    id={processRowActions}
                    dropDownButtonClassName={"row-actions-menu-button-selector"}
                    dropDownButtonId={"actions-" + elemKey}
                    dropDownContentPullRight={true}
                >
                    {getActions()}
                </DropdownButtonWithState>
            )}
        </>
    );
}

export interface ActionMenuDispatchProps {
    onRunOnce: (ids: number[], agentGroupName: string) => void;
    onActivate: (ids: number[], agentGroupName: string) => void;
    onDeactivate: (ids: number[], agentGroupName: string) => void;
    onCopyProcesses: () => void;
    onRebuildProcesses: () => void;
    onDeleteProcesses: () => void;
    onCleanupOldVersions: (processGuid: string) => void;
    onOpenTagModal: () => void;
    onOpenDeployDialog: (ids: number[], isSwitchVersion: boolean) => void;
    onOpenLogLevelDialog: (ids: number[]) => void;
    onExportProcesses: (ids: number[], agentGroupName: string) => void;
    onOpenChangeLog: (id: number) => void;
    onPin: () => void;
    onOpenProcessVariablesEditorDialog: (id: number) => void;
    onCreateTemplateFromProcess: (id: number) => void;
    onUnlinkTemplate: (id: number) => void;
}

interface ActionMenuOwnProps {
    processIds: number[];
    labelComponent: React.ReactElement;
    editLink?: string;
    showProcessVariableEditItem?: boolean;
    instanceList?: boolean;
    isBatch?: boolean;
    managedState?: {
        open: boolean;
        onOpen: () => void;
        onHide: () => void;
    }
}

interface ActionMenuStateProps {
    isSubprocessList: boolean;
    currentAgentGroupName: string;
    processGuids: string[];
    permissions: { [activity: string]: boolean };
    hasTemplate: boolean;
}

type ActionMenuProps = ActionMenuOwnProps & ActionMenuStateProps & ActionMenuDispatchProps;

const mapStateToProps = (state: State, ownProps: ActionMenuOwnProps): ActionMenuStateProps => {
    const hasTemplate = state.processes.some((f) => f.id === ownProps.processIds[0] && f.template);
    return {
        isSubprocessList: state.isSubprocessList,
        currentAgentGroupName: state.currentAgentGroupName,
        processGuids: getProcessGuids(ownProps.processIds, state.processes),
        permissions: state.userPermissions,
        hasTemplate
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>, ownProps: ActionMenuOwnProps): ActionMenuDispatchProps => {
    return {
        onRunOnce: (ids: number[], agentGroupName: string) => dispatch(RunOnceActions.runOnce(ids, agentGroupName)),
        onActivate: (ids: number[], agentGroupName: string) => dispatch(Actions.activate(ids, agentGroupName)),
        onDeactivate: (ids: number[], agentGroupName: string) => dispatch(Actions.deactivate(ids, agentGroupName)),
        onCopyProcesses: () => dispatch(Actions.copyProcesses(ownProps.processIds)),
        onRebuildProcesses: () => dispatch(Actions.createNewProcessVersions(ownProps.processIds)),
        onDeleteProcesses: () => dispatch(Actions.openConfirmDeleteProcessesDialog(ownProps.processIds)),
        onCleanupOldVersions: (processGuid) => dispatch(CleanOldVersionsActions.openCleanVersionsDialog(processGuid)),
        onOpenTagModal: () => dispatch(Actions.openTagModal(ownProps.processIds)),
        onOpenDeployDialog: (ids: number[], isSwitchVersion: boolean) => dispatch(DeployActions.openDeployModal(ids, isSwitchVersion)),
        onOpenLogLevelDialog: (ids: number[]) => dispatch(LogLevelActions.showLogLevelSettings(ids)),
        onExportProcesses: (ids: number[], agentGroupName: string) => dispatch(Actions.exportProcesses(ids, agentGroupName)),
        onOpenChangeLog: (id: number) => dispatch(Actions.openChangeLogModal(id)),
        onPin: () => dispatch(Actions.pinProcesses(ownProps.processIds)),
        onOpenProcessVariablesEditorDialog: (id: number) => dispatch(Actions.triggerOpenProcessVariablesEditorModal(id)),
        onCreateTemplateFromProcess: (id: number) => dispatch(Actions.setSelectCreateTemplateFromProcessModeModal(id)),
        onUnlinkTemplate: (id: number) => dispatch(Actions.unlinkTemplate(id))
    };
};

function getProcessGuids(processIds: number[], processes: Process[]) {
    return processes
        .filter((f) => processIds
            .some((s) => f.id === s))
        .map((x) => x.uniqueIdentifier);
}

export default connect(mapStateToProps, mapDispatchToProps)(ActionMenu);
